import { ProcedureDocumentType, DbRef } from '@caresend/types';
import { getProcedureDocumentsByType, isNullish, formatAddressUS, AddressFormatType, formatDateOfBirth, capitalize, formatLocalDate } from '@caresend/utils';
import { defineComponent, computed, ref, onMounted } from 'vue';
import __vue_component__$4 from '../../../../../alerts/Pane/Pane.vue.js';
import __vue_component__$1 from '../../../../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__ from '../../../../../cards/BasicCard/BasicCard.vue.js';
import __vue_component__$2 from '../../../../../inputs/FilePickerViewer.vue.js';
import __vue_component__$3 from './components/ManualReqFormInfo.vue.js';
import { usePortalStore } from '../../../store/portal/index.js';
import __vue_component__$5 from '../../../../../ui/QRCode/QRCode.vue.js';

var script = defineComponent({
    name: 'LabOrder',
    components: {
        BasicCard: __vue_component__,
        ButtonComponent: __vue_component__$1,
        FilePickerViewer: __vue_component__$2,
        ManualReqFormInfo: __vue_component__$3,
        Pane: __vue_component__$4,
        QRCode: __vue_component__$5,
    },
    props: {
        workflowItem: {
            type: Object,
            required: true,
        },
    },
    setup(props) {
        const portalStore = usePortalStore();
        const procedure = computed(() => portalStore.procedures.get(props.workflowItem.recordMeta.referenceID));
        const waypoint = computed(() => {
            var _a;
            const waypointID = (_a = portalStore.waypointActions
                .get(props.workflowItem.recordMeta.parent.referenceID)) === null || _a === void 0 ? void 0 : _a.waypointID;
            if (!waypointID)
                return null;
            return portalStore.waypoints.get(waypointID);
        });
        const prescriber = computed(() => { var _a, _b; return (_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.computed.prescriber) !== null && _b !== void 0 ? _b : undefined; });
        const prescriberName = computed(() => { var _a, _b, _c, _d; return `${(_b = (_a = prescriber.value) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.firstName} ${(_d = (_c = prescriber.value) === null || _c === void 0 ? void 0 : _c.info) === null || _d === void 0 ? void 0 : _d.lastName}`; });
        const instructionalRequisitionForm = computed(() => {
            var _a, _b, _c;
            return (_c = (_b = getProcedureDocumentsByType((_a = procedure.value) !== null && _a !== void 0 ? _a : undefined, ProcedureDocumentType.INSTRUCTIONAL_REQUISITION_FORM)) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.file;
        });
        const placeAccount = computed(() => {
            var _a, _b, _c, _d;
            const id = (_c = Object.keys((_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.placeAccounts) !== null && _b !== void 0 ? _b : {})) === null || _c === void 0 ? void 0 : _c[0];
            if (isNullish(id))
                return null;
            return (_d = portalStore[DbRef.PLACE_ACCOUNTS].getRaw(id)) !== null && _d !== void 0 ? _d : null;
        });
        const placeGroup = computed(() => {
            var _a, _b;
            if (!((_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.placeID))
                return null;
            const place = portalStore[DbRef.PLACES].getRaw(waypoint.value.placeID);
            if (!(place === null || place === void 0 ? void 0 : place.placeGroupID))
                return null;
            return (_b = portalStore[DbRef.PLACE_GROUPS].getRaw(place.placeGroupID)) !== null && _b !== void 0 ? _b : null;
        });
        const placeAccountOfficeAddress = computed(() => {
            var _a, _b;
            if (!((_b = (_a = placeAccount.value) === null || _a === void 0 ? void 0 : _a.officeInfo) === null || _b === void 0 ? void 0 : _b.address))
                return;
            return formatAddressUS(placeAccount.value.officeInfo.address, undefined, AddressFormatType.MULTI_LINE);
        });
        const patient = computed(() => { var _a, _b; return (_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.computed.patient) !== null && _b !== void 0 ? _b : undefined; });
        const patientDOB = computed(() => {
            var _a, _b;
            if (!((_b = (_a = patient.value) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.dateOfBirth))
                return;
            return formatDateOfBirth(patient.value.info.dateOfBirth);
        });
        const collectionTimestamp = computed(() => { var _a, _b, _c; return (_c = Object.values((_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.samples) !== null && _b !== void 0 ? _b : {})[0]) === null || _c === void 0 ? void 0 : _c.collectionTimestamp; });
        const formatCollectionTimeStamp = (format) => formatLocalDate(collectionTimestamp.value, format);
        const collectionDate = computed(() => formatCollectionTimeStamp('M/D/YYYY'));
        const collectionTime = computed(() => formatCollectionTimeStamp('h:mm a z'));
        const isManualReqFormInfoOpen = ref(false);
        const contentRef = ref(null);
        const footerWrapperRef = ref(null);
        onMounted(() => {
            if (contentRef.value && footerWrapperRef.value) {
                const footerHeight = footerWrapperRef.value.clientHeight;
                contentRef.value.style.paddingBottom = `${footerHeight}px`;
            }
        });
        return {
            capitalize,
            collectionDate,
            collectionTime,
            collectionTimestamp,
            contentRef,
            footerWrapperRef,
            instructionalRequisitionForm,
            isManualReqFormInfoOpen,
            patient,
            patientDOB,
            placeAccount,
            placeAccountOfficeAddress,
            placeGroup,
            prescriber,
            prescriberName,
            procedure,
        };
    },
});

export { script as default };
